<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import Layout from '@client/pages/bizcards/studio/designer/layout.svelte';

  import { BizCardsService } from '@client/services/bizcards';

  import { id, loadTemplate, saveBizcard, updatedRulename } from '@client/stores/bizcard';

  import StudioForm from './shared/studio-form.svelte';

  const { addNotification } = getNotificationsContext();

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  let selectedTemplate = null;

  const handleTemplateSelect = (template) => {
    selectedTemplate = template;
  };

  const loadTemplateItem = async (item) => {
    await loadTemplate(item.id);
  };

  const executeCreateBizcard = async () => {
    const success = await saveBizcard();

    if (!success) {
      throw new Error($format('label.FAILURE.CREATE_BUSINESS_CARD'));
    }

    const bizcardName = get(updatedRulename);

    step.data = {
      id: bizcardName
    };

    return { rulename: bizcardName };
  };

  const rollbackCreateBizcard = async () => {
    if (!step.data?.id) {
      return;
    }

    try {
      const bizcard = await BizCardsService.getById(step.data.id);

      if (bizcard) {
        const response = await BizCardsService.delete(bizcard);

        if (response.status >= 400) {
          addNotification({
            text: $format('label.FAILURE.DELETE_BUSINESS_CARD', { values: { rulename: step.data.id } }),
            type: 'error',
            autohide: 2500,
            position: 'top-right'
          });
          throw new Error($format('label.FAILURE.DELETE_BUSINESS_CARD_ROLLBACK'));
        }

        addNotification({
          text: $format('label.SUCCESS.DELETE', { values: { type: step.data.id } }),
          type: 'success',
          autohide: 2500,
          position: 'top-right'
        });
      }
    } catch (error) {
      console.error('Failed to rollback bizcard creation:', error);
      throw error;
    }
  };
</script>

<StudioForm
  bind:nextDisabled
  bind:step
  bind:selectedItem={selectedTemplate}
  {onExecute}
  {onRollback}
  mode="create"
  firstStepTitle={$format('label.SELECT_TEMPLATE')}
  secondStepTitle={$format('label.CONFIGURE_BUSINESS_CARD')}
  executeLabel="label.CREATE_BUSINESS_CARD"
  onLoadItem={loadTemplateItem}
  onExecuteAction={executeCreateBizcard}
  onRollbackAction={rollbackCreateBizcard}
>
  <div slot="selection">
    <Layout onSelect={handleTemplateSelect} />
  </div>
</StudioForm>

<style lang="scss">
  :global(
      .bizcard-studio-form .template-selection-wrapper .business-card-templates .business-card-template .card-wrapper
    ) {
    width: 161.5px !important;
    height: 343.4px !important;
  }

  :global(
      .bizcard-studio-form
        .template-selection-wrapper
        .business-card-templates
        .business-card-template
        .card-wrapper
        iframe
    ) {
    width: 323px !important;
    height: 686.8px !important;
  }
</style>
